import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { api, enviarFormulario } from '../../api';
import { data, nomeDoContato, telefoneDigitadoLegivel, telefoneLegivel } from '../../formato';
import { useSessao } from '../../sessao';
import { Area, Aviso, Dialogo, ErroDaApi, Escolha, Selo, ZonaDeArquivo } from '../../ui';
import { buscarRespostas, primeiroNome, semAcento, tamanho, type RespostaPronta } from './Conversa';
import { faltaPara, Rodape } from './JanelasDaConversa';
import './comercial.css';

/**
 * A Nova conversa — onda 554 (Parte B · Contatos e conversa ativa, Tarefa 7;
 * spec §3.1, quadro 1 do canvas). A equipe começa a conversa de WhatsApp com
 * quem nunca escreveu, ou com quem não escreve há tempo
 * (`POST /comercial/conversas`, Tarefa 4).
 *
 *  · **Sai pelo** — o WhatsApp da equipe que está ligado (o central, em
 *    teste ou no ar) e «Meu WhatsApp comercial», que só aparece para o DONO
 *    de um canal de carteira ligado (B4; a diretoria vê as carteiras na
 *    caixa, mas não sai por elas). E-mail fica de fora.
 *  · **Para** — um campo só, que busca nome do contato, nome do cliente,
 *    telefone ou e-mail, no Comercial e no cadastro
 *    (`/comercial/contatos/busca`). Cada sugestão diz o cliente, o WhatsApp
 *    e o cargo, e duas marcas: «Conversa aberta» (neste canal) e «Não
 *    chamar» (pediu para parar, B7 — não se escolhe, e diz a data). A última
 *    linha é «Usar um número novo»: o número digitado, com ou sem +55, com
 *    ou sem máscara, aparece formatado; se ele já é de um contato, a
 *    sugestão é o contato (a busca acha pelo número normalizado) e a linha
 *    do número some.
 *  · **Conversa aberta no canal** — o aviso diz, e «Enviar» leva até ela com
 *    a mensagem escrita no compositor (a API devolve `existente`, nada
 *    enviado).
 *  · **Primeira mensagem** — obrigatória; um anexo, como na Caixa; «/» no
 *    começo abre as respostas prontas.
 *  · **Abrir oportunidade** — só para quem edita o Comercial (`crm.editar`):
 *    marcada quando o contato não tem oportunidade aberta (e no número
 *    novo); desmarcada quando já tem, e a conversa liga à mais recente (o
 *    painel da conversa permite trocar). Com a oportunidade dada por quem
 *    abre a janela (`inicial.oportunidadeId`, a ligação da Tarefa 8), a
 *    conversa liga a ela, e a caixa não aparece — enquanto o destino for o
 *    contato dela: trocado por outro contato (ou por um número que é de
 *    outro), a oportunidade dada cai e a caixa volta (revisão final, I1).
 *  · **O contato sem WhatsApp** (o fixo da ligação ou da ficha) — «Trocar» e
 *    o número escrito no «Para»: o número vai junto com o contato
 *    (`contatoId` + `numero`) e vira o WhatsApp DELE, sem criar outro
 *    contato (revisão final, I1). O número que é o telefone de um contato
 *    sem WhatsApp segue a oportunidade aberta dele na caixa (M5).
 *
 * Enviado, a tela abre a conversa (`aoAbrir`). As recusas da API (403, 409:
 * a carteira de outra pessoa, o canal desligado, quem pediu para parar)
 * aparecem como vêm, em frase inteira.
 */

interface Canal {
  codigo: string; nome: string; tipo: string; situacao: string; visibilidade: string;
  identificador: string | null; meu: boolean;
}

interface Achado {
  id: string; nome: string; salvo: boolean; whatsappPerfil: string | null; cargo: string | null;
  whatsapp: string | null; telefone: string | null; clientes: { id: string; nome: string }[];
  conversaAbertaId: string | null; naoReceberEm: string | null; oportunidadeAbertaId: string | null;
}

type Destino = { tipo: 'contato'; c: Achado } | { tipo: 'numero'; n: string };

export interface InicialDaNovaConversa {
  contatoId?: string;
  numero?: string;
  /** A oportunidade a que a conversa nasce ligada (a da conversa de telefone, Tarefa 8). */
  oportunidadeId?: string;
  /** A origem da oportunidade aberta junto (a ligação abre como `telefone`). */
  origem?: 'whatsapp' | 'telefone';
}

const LIGADO = ['teste', 'no_ar'];

/**
 * O número digitado, normalizado como a API faz no Brasil: 10 ou 11 dígitos
 * são o DDD e o número (ganham o 55); 12 ou 13 com o 55 já são. Fora disso,
 * não é um número de WhatsApp que a tela saiba escrever.
 */
export function numeroDigitado(t: string): string | null {
  if (!/^[+()\d .-]+$/.test(t.trim())) return null;
  const d = t.replace(/\D/g, '');
  if (/^[1-9][1-9]\d{8,9}$/.test(d)) return `55${d}`;
  if (/^55[1-9][1-9]\d{8,9}$/.test(d)) return d;
  return null;
}

/** O nome de quem chegou sozinho é o número: ao lado, o perfil do WhatsApp em cinza («~Carla M.»). */
function NomeDoAchado({ c }: { c: Achado }) {
  return (
    <>
      <b>{nomeDoContato(c.nome)}</b>
      {!c.salvo && c.whatsappPerfil && <span className="det">~{c.whatsappPerfil}</span>}
      {c.clientes.length > 0 && <span className="nova-conversa-cliente"> · {c.clientes.map((x) => x.nome).join(', ')}</span>}
    </>
  );
}

/** A segunda linha da sugestão: o WhatsApp (ou o telefone, sem WhatsApp) e o cargo; ou o «pediu para parar». */
function detalheDoAchado(c: Achado): string {
  if (c.naoReceberEm) return `Pediu para não receber mensagens em ${data(c.naoReceberEm)}`;
  const zap = c.whatsapp ? `WhatsApp ${telefoneLegivel(c.whatsapp)}`
    : c.telefone ? `Telefone ${telefoneDigitadoLegivel(c.telefone)} · sem WhatsApp cadastrado`
      : 'sem WhatsApp cadastrado';
  return [zap, c.cargo].filter(Boolean).join(' · ');
}

export function JanelaNovaConversa({ aberta, aoFechar, aoAbrir, inicial }: {
  aberta: boolean;
  aoFechar(): void;
  /** A conversa aberta (nova, ou a que já existia — então com o texto como rascunho). */
  aoAbrir(conversaId: string, rascunho?: string): void;
  inicial?: InicialDaNovaConversa;
}) {
  const { eu, pode } = useSessao();
  const editaOComercial = pode('crm.editar');
  const id = useId();

  const [canais, setCanais] = useState<Canal[] | null>(null);
  const [canal, setCanal] = useState<string | null>(null);
  const [busca, setBusca] = useState('');
  const [achados, setAchados] = useState<Achado[] | null>(null);
  const [aberto, setAberto] = useState(false);
  const [marcado, setMarcado] = useState(0);
  const [destino, setDestino] = useState<Destino | null>(null);
  /* O contato de quem o número escolhido é o telefone (sem WhatsApp gravado). */
  const [telefoneDe, setTelefoneDe] = useState<Achado | null>(null);
  /* O contato sem WhatsApp de quem o número escrito passa a ser o WhatsApp (vai junto no pedido). */
  const [donoDoNumero, setDonoDoNumero] = useState<Achado | null>(null);
  /* De quem é a oportunidade dada por quem abriu (o contato, ou o dono do número que veio), e se a pessoa
     já escolheu outro destino no «Para». */
  const [contatoDaDada, setContatoDaDada] = useState<string | null>(null);
  const [escolheu, setEscolheu] = useState(false);
  /* Com alguém escolhido, o «Para» mostra o escolhido; «Trocar» volta ao campo de busca. */
  const [trocando, setTrocando] = useState(false);
  const [texto, setTexto] = useState('');
  const [anexando, setAnexando] = useState(false);
  const [anexo, setAnexo] = useState<{ arquivoId: string; nome: string; tamanho: number } | null>(null);
  const [subindo, setSubindo] = useState<number | null>(null);
  const [abrirOportunidade, setAbrirOportunidade] = useState(true);
  const [prontas, setProntas] = useState<RespostaPronta[] | null>(null);
  const [pronta, setPronta] = useState(0);
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  const caixa = useRef<HTMLDivElement>(null);
  const campoPara = useRef<HTMLInputElement>(null);

  /* Aberta: tudo do zero, os canais e o que veio de quem abriu (o contato ou o número). */
  useEffect(() => {
    if (!aberta) return undefined;
    setBusca(''); setAchados(null); setAberto(false); setDestino(null); setTrocando(false); setTexto(''); setAnexando(false);
    setTelefoneDe(null); setDonoDoNumero(null); setContatoDaDada(inicial?.contatoId ?? null); setEscolheu(false);
    setAnexo(null); setErro(null); setAbrirOportunidade(true); setCanal(null); setCanais(null);
    let vale = true;
    api<Canal[]>('/comercial/canais').then((l) => {
      if (!vale) return;
      const saem = l.filter((c) => c.tipo === 'whatsapp' && LIGADO.includes(c.situacao)
        && (c.visibilidade === 'equipe' || c.meu));
      setCanais(saem);
      /* O padrão é o da equipe no ar (o central); depois o da equipe em teste; a carteira só se for a única. */
      const equipe = saem.filter((c) => c.visibilidade === 'equipe');
      setCanal((equipe.find((c) => c.situacao === 'no_ar') ?? equipe[0] ?? saem[0])?.codigo ?? null);
    }).catch((e) => { if (vale) { setCanais([]); setErro(e); } });
    return () => { vale = false; };
  }, [aberta]);

  /* O contato que veio de quem abriu, e a conversa aberta dele no canal escolhido (muda com o canal). */
  const contatoId = destino?.tipo === 'contato' ? destino.c.id : inicial?.contatoId ?? null;
  useEffect(() => {
    if (!aberta || !canal || !contatoId) return undefined;
    if (destino && destino.tipo !== 'contato') return undefined;
    let vale = true;
    api<Achado[]>(`/comercial/contatos/busca?id=${encodeURIComponent(contatoId)}&canal=${encodeURIComponent(canal)}`)
      .then((l) => { if (vale && l[0]) escolherContato(l[0], false); })
      .catch(() => undefined);
    return () => { vale = false; };
  }, [aberta, canal, contatoId]); // eslint-disable-line react-hooks/exhaustive-deps

  /* O número que veio de quem abriu: vira o contato, se for de um. */
  useEffect(() => {
    if (!aberta || !inicial?.numero || inicial.contatoId) return;
    const n = numeroDigitado(inicial.numero);
    if (n) setDestino({ tipo: 'numero', n });
    else setBusca(inicial.numero);
  }, [aberta]); // eslint-disable-line react-hooks/exhaustive-deps

  const termo = busca.trim();
  const numero = numeroDigitado(termo);
  const numeroDoDestino = destino?.tipo === 'numero' ? destino.n : null;
  /* A busca: o que se digita no «Para», ou o número que veio de quem abriu (para virar o contato). */
  const consulta = termo || numeroDoDestino || '';
  useEffect(() => {
    if (!aberta || !canal || consulta.length < 2) { setAchados(null); return undefined; }
    let vale = true;
    const t = window.setTimeout(() => {
      api<Achado[]>(`/comercial/contatos/busca?q=${encodeURIComponent(consulta)}&canal=${encodeURIComponent(canal)}`)
        .then((l) => {
          if (!vale) return;
          setAchados(l); setMarcado(0);
          /* O número escolhido já é de um contato: a escolha vira o contato. */
          if (!termo && numeroDoDestino) {
            const dono = l.find((c) => c.whatsapp === numeroDoDestino);
            const doInicial = !!inicial?.numero && numeroDigitado(inicial.numero) === numeroDoDestino;
            if (dono && !dono.naoReceberEm) {
              if (doInicial) setContatoDaDada((x) => x ?? dono.id);
              escolherContato(dono, false);
            } else if (!dono) {
              /* O número é o telefone de um contato sem WhatsApp (o celular da ficha do cliente, Tarefa 8). */
              const tz = l.find((c) => !c.whatsapp && c.telefone && numeroDigitado(c.telefone) === numeroDoDestino) ?? null;
              setTelefoneDe(tz);
              if (tz) {
                if (doInicial) setContatoDaDada((x) => x ?? tz.id);
                /* É de outro contato, e não do escolhido sem WhatsApp: o destino passa a ser ele. */
                setDonoDoNumero((x) => (x && x.id !== tz.id ? null : x));
                /* M5: a caixa segue a oportunidade aberta de quem tem o número. */
                setAbrirOportunidade(!tz.oportunidadeAbertaId);
              }
            }
          }
        })
        .catch(() => { if (vale) setAchados([]); });
    }, 250);
    return () => { vale = false; window.clearTimeout(t); };
  }, [aberta, canal, consulta]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Clique fora fecha a lista — e não escolhe. */
  useEffect(() => {
    if (!aberto) return undefined;
    const fora = (e: MouseEvent) => {
      if (caixa.current && !caixa.current.contains(e.target as Node)) setAberto(false);
    };
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [aberto]);

  function escolherContato(c: Achado, limpar = true) {
    setDestino({ tipo: 'contato', c });
    setDonoDoNumero(null);
    setAbrirOportunidade(!c.oportunidadeAbertaId);
    if (limpar) { setBusca(''); setAberto(false); setTrocando(false); }
  }

  /* As linhas da lista: os contatos e, por último, o número. O número que já é de um contato não aparece. */
  const lista = achados ?? [];
  const numeroEhDeAlguem = !!numero && lista.some((c) => c.whatsapp === numero);
  const linhas: ({ tipo: 'contato'; c: Achado } | { tipo: 'numero' })[] = [
    ...lista.map((c) => ({ tipo: 'contato' as const, c })),
    ...(numeroEhDeAlguem ? [] : [{ tipo: 'numero' as const }]),
  ];
  const escolher = (i: number) => {
    const l = linhas[i];
    if (!l) return;
    if (l.tipo === 'contato') {
      if (l.c.naoReceberEm) return;
      setEscolheu(true);
      escolherContato(l.c);
      return;
    }
    if (!numero) { setBusca(''); campoPara.current?.focus(); return; }
    /* O número escrito para o contato sem WhatsApp (ou de novo para o mesmo) vira o WhatsApp dele. */
    const dono = destino?.tipo === 'contato' ? (destino.c.whatsapp ? null : destino.c) : donoDoNumero;
    setEscolheu(true);
    setDonoDoNumero(dono);
    setTelefoneDe(null);
    setDestino({ tipo: 'numero', n: numero });
    setAbrirOportunidade(!dono?.oportunidadeAbertaId);
    setBusca(''); setAberto(false); setTrocando(false);
  };
  const aoTeclarPara = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setAberto(true);
      setMarcado((m) => {
        const n = linhas.length;
        if (!n) return 0;
        return e.key === 'ArrowDown' ? (m + 1) % n : (m - 1 + n) % n;
      });
      return;
    }
    if (e.key === 'Enter') {
      if (aberto && linhas[marcado]) { e.preventDefault(); escolher(marcado); }
      return;
    }
    if (e.key === 'Escape' && aberto) { e.preventDefault(); setAberto(false); }
  };

  /* As respostas prontas: «/» no começo da mensagem, até o primeiro espaço. */
  const buscaPronta = /^\/(\S*)$/.exec(texto)?.[1] ?? null;
  useEffect(() => {
    if (buscaPronta === null || prontas) return;
    buscarRespostas().then(setProntas).catch(() => setProntas([]));
  }, [buscaPronta, prontas]);
  const opcoesProntas = useMemo(() => {
    if (buscaPronta === null || !prontas) return [];
    const q = semAcento(buscaPronta);
    return prontas.filter((r) => semAcento(r.atalho ?? '').startsWith(q) || semAcento(r.titulo).includes(q)).slice(0, 8);
  }, [buscaPronta, prontas]);
  useEffect(() => { setPronta(0); }, [buscaPronta]);
  const listaPronta = buscaPronta !== null && opcoesProntas.length > 0;
  const aoTeclarMensagem = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!listaPronta) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setPronta((i) => (i + 1) % opcoesProntas.length); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setPronta((i) => (i - 1 + opcoesProntas.length) % opcoesProntas.length); return; }
    if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); setTexto(opcoesProntas[pronta].texto); return; }
    if (e.key === 'Escape') { e.preventDefault(); setTexto(''); }
  };

  const subir = async (f: File | undefined) => {
    if (!f) return;
    setErro(null);
    setSubindo(0);
    try {
      const fd = new FormData();
      fd.append('arquivo', f);
      setAnexo(await enviarFormulario<{ arquivoId: string; nome: string; tamanho: number }>(
        '/comercial/anexos', fd, (p) => setSubindo(p)));
      setAnexando(false);
    } catch (e) { setErro(e); } finally { setSubindo(null); }
  };

  const oCanal = canais?.find((c) => c.codigo === canal) ?? null;
  const nomeDoCanal = (c: Canal) => (c.visibilidade === 'carteira' ? 'Meu WhatsApp comercial' : c.nome);
  const contato = destino?.tipo === 'contato' ? destino.c : null;
  const semWhatsapp = !!contato && !contato.whatsapp;
  const conversaAberta = contato?.conversaAbertaId ?? null;
  /* A pessoa de quem a conversa vai ser: o contato escolhido, ou o dono do número escrito. */
  const pessoa = contato ?? (destino?.tipo === 'numero' ? donoDoNumero ?? telefoneDe : null);
  /* A oportunidade dada por quem abriu vale enquanto o destino for o contato dela (ou, sem saber de quem
     é, enquanto a pessoa não escolheu outro destino). */
  const ligadaDada = inicial?.oportunidadeId
    && (contatoDaDada ? !destino || pessoa?.id === contatoDaDada : !escolheu) ? inicial.oportunidadeId : null;
  /* Com a oportunidade dada valendo, a caixa some: a conversa liga a ela. */
  /* Com a conversa já aberta no canal, «Enviar» só leva até ela: nada se abre, e a caixa some. */
  const mostraOportunidade = editaOComercial && !ligadaDada && !conversaAberta;

  /*
   * O Esc de uma lista aberta (o «Para» ou as respostas prontas) fecha a
   * lista, não a janela: o `<dialog>` nativo pede para fechar (`cancel`)
   * antes do React ver a tecla, e o `Dialogo` chama o `aoFechar` daqui.
   */
  const listas = useRef({ para: false, prontas: false });
  listas.current = { para: aberto && termo.length >= 1, prontas: listaPronta };
  const pedirParaFechar = () => {
    if (listas.current.para) { setAberto(false); return; }
    if (listas.current.prontas) { setTexto(''); return; }
    aoFechar();
  };

  const falta = faltaPara('enviar', [
    ...(canais && !oCanal ? ['um WhatsApp ligado para sair'] : []),
    ...(!destino ? ['para quem (um contato ou um número)'] : semWhatsapp ? [`o WhatsApp de ${primeiroNome(nomeDoContato(contato!.nome))}`] : []),
    ...(texto.trim() === '' || buscaPronta !== null ? ['a primeira mensagem'] : []),
  ]);

  const enviar = async () => {
    if (!destino || !canal) return;
    setOcupado(true);
    setErro(null);
    const ligarA = ligadaDada
      ?? (editaOComercial && !abrirOportunidade && pessoa?.oportunidadeAbertaId ? pessoa.oportunidadeAbertaId : null);
    const abre = mostraOportunidade && abrirOportunidade;
    try {
      const r = await api<{ conversaId: string; existente: boolean }>('/comercial/conversas', {
        metodo: 'POST',
        corpo: {
          canal,
          ...(destino.tipo === 'contato' ? { contatoId: destino.c.id }
            : donoDoNumero ? { contatoId: donoDoNumero.id, numero: destino.n } : { numero: destino.n }),
          texto: texto.trim(),
          ...(anexo ? { arquivoId: anexo.arquivoId } : {}),
          ...(abre ? { abrirOportunidade: true, ...(inicial?.origem ? { origem: inicial.origem } : {}) } : {}),
          ...(!abre && ligarA ? { oportunidadeId: ligarA } : {}),
        },
      });
      aoAbrir(r.conversaId, r.existente ? texto.trim() : undefined);
    } catch (e) { setErro(e); } finally { setOcupado(false); }
  };

  const listaId = `${id}-para`;
  const idPronta = `${id}-prontas`;
  return (
    <Dialogo aberto={aberta} titulo="Nova conversa" aoFechar={pedirParaFechar} classe="janela-nova-conversa">
      <div className="nova-conversa">
        {canais === null ? <p className="janela-explica">Carregando os canais</p>
          : canais.length === 0 ? (
            <Aviso tipo="atencao">
              Nenhum WhatsApp está ligado para sair agora. Peça a quem configura o atendimento para ligar o WhatsApp
              central em Ajustes do Comercial.
            </Aviso>
          ) : (
            <Escolha rotulo="Sai pelo" limpavel={false} valor={canal} aoEscolher={(v) => { if (v) setCanal(v); }}
              classe="nova-conversa-canais"
              opcoes={canais.map((c) => ({
                valor: c.codigo, texto: nomeDoCanal(c),
                detalhe: c.visibilidade === 'carteira'
                  ? `Carteira de ${primeiroNome(eu?.nome) || 'você'} · só você vê`
                  : c.identificador ? telefoneLegivel(c.identificador) : undefined,
              }))} />
          )}

        <div className="campo escolha-codigo largo nova-conversa-para" ref={caixa}>
          <label className="rotulo" htmlFor={`${id}-campo`}>Para <span className="nova-conversa-rotulo-aux">· nome, cliente ou número</span></label>
          {destino && !trocando ? (
            <div className="nova-conversa-escolhido" id={`${id}-campo`} tabIndex={-1}>
              <p>
                {destino.tipo === 'contato' ? (
                  <>
                    <strong>{nomeDoContato(destino.c.nome)}</strong>
                    {destino.c.clientes.length > 0 && <> · {destino.c.clientes.map((x) => x.nome).join(', ')}</>}
                    <span className="sub">{detalheDoAchado(destino.c)}</span>
                  </>
                ) : (
                  <>
                    <strong>{telefoneLegivel(destino.n)}</strong>
                    <span className="sub">
                      {donoDoNumero ? `WhatsApp de ${nomeDoContato(donoDoNumero.nome)}: o número fica gravado no contato.`
                        : telefoneDe ? `Telefone de ${nomeDoContato(telefoneDe.nome)}, que não tem WhatsApp gravado.`
                          : 'Número novo, ainda sem contato.'}
                    </span>
                  </>
                )}
              </p>
              <button type="button" className="botao secundario"
                onClick={() => { setTrocando(true); window.setTimeout(() => campoPara.current?.focus(), 0); }}>
                Trocar
              </button>
            </div>
          ) : (
            <div className="campo-codigo">
              <input id={`${id}-campo`} ref={campoPara} type="text" role="combobox" aria-expanded={aberto}
                aria-controls={listaId} aria-autocomplete="list" autoComplete="off" inputMode="search"
                aria-activedescendant={aberto && linhas[marcado] && termo.length >= 2 && achados ? `${listaId}-${marcado}` : undefined}
                value={busca} placeholder="Digite o nome, o cliente, o telefone ou o e-mail."
                onFocus={() => setAberto(true)} onKeyDown={aoTeclarPara}
                onChange={(e) => { setBusca(e.currentTarget.value); setAberto(true); }} />
            </div>
          )}
          {(!destino || trocando) && aberto && termo.length >= 1 && (
            <ul className="lista-codigos nova-conversa-sugestoes" id={listaId} role="listbox" aria-label="Contatos encontrados">
              {termo.length < 2 ? (
                <li className="nada" role="presentation">Digite duas letras ou mais, ou o número com o DDD.</li>
              ) : achados === null ? (
                <li className="nada" role="presentation">Procurando…</li>
              ) : (
                <>
                  {lista.length > 0 && <li className="nada nova-conversa-grupo" role="presentation">Contatos do cadastro e do Comercial</li>}
                  {lista.length === 0 && <li className="nada" role="presentation">Nenhum contato com «{termo}».</li>}
                  {linhas.map((l, i) => (l.tipo === 'contato' ? (
                    <li key={l.c.id} id={`${listaId}-${i}`} role="option" data-contato={l.c.id} aria-selected={contato?.id === l.c.id}
                      aria-disabled={l.c.naoReceberEm ? true : undefined}
                      className={`${i === marcado ? 'marcado' : ''}${l.c.naoReceberEm ? ' nao-chamar' : ''}`}
                      onMouseEnter={() => setMarcado(i)}
                      onMouseDown={(e) => { e.preventDefault(); escolher(i); }}>
                      <span className="nova-conversa-sug">
                        <span className="nova-conversa-sug-nome"><NomeDoAchado c={l.c} /></span>
                        <span className="det">{detalheDoAchado(l.c)}</span>
                      </span>
                      {l.c.naoReceberEm ? <Selo tom="critico">Não chamar</Selo>
                        : l.c.conversaAbertaId ? <Selo tom="andamento">Conversa aberta</Selo> : null}
                    </li>
                  ) : (
                    <li key="numero" id={`${listaId}-${i}`} role="option" data-numero={numero ?? ''} aria-selected={false}
                      className={i === marcado ? 'marcado' : undefined}
                      onMouseEnter={() => setMarcado(i)}
                      onMouseDown={(e) => { e.preventDefault(); escolher(i); }}>
                      <span className="nova-conversa-sug">
                        <span className="nova-conversa-sug-nome">
                          {numero ? <>Usar um número novo: <b>{telefoneLegivel(numero)}</b></> : 'Usar um número novo'}
                        </span>
                        <span className="det">
                          {!numero ? 'Digite com DDD: +55 (47) 9…'
                            : semWhatsapp ? `Fica gravado como WhatsApp de ${nomeDoContato(contato!.nome)}`
                              : 'Ainda não é de nenhum contato'}
                        </span>
                      </span>
                    </li>
                  )))}
                </>
              )}
            </ul>
          )}
        </div>

        {contato && conversaAberta && oCanal && (
          <Aviso tipo="info">
            <span>
              {primeiroNome(nomeDoContato(contato.nome))} já tem conversa aberta no {nomeDoCanal(oCanal)}. <strong>Enviar</strong> leva
              você até ela, com a mensagem já escrita.
            </span>
          </Aviso>
        )}
        {semWhatsapp && (
          <Aviso tipo="atencao">
            <span>
              {nomeDoContato(contato!.nome)} não tem WhatsApp gravado. Escreva o número de WhatsApp dele no «Para», ou
              grave o WhatsApp no contato.
            </span>
          </Aviso>
        )}

        <div className="escolha-codigo largo nova-conversa-mensagem">
          <Area rotulo="Primeira mensagem" rows={4} value={texto} onChange={(e) => setTexto(e.target.value)}
            onKeyDown={aoTeclarMensagem} role="combobox" aria-expanded={listaPronta} aria-controls={idPronta}
            aria-autocomplete="list" aria-activedescendant={listaPronta ? `${idPronta}-${pronta}` : undefined}
            dica="Digite «/» para as respostas prontas." />
          {listaPronta && (
            <ul className="lista-codigos nova-conversa-prontas" id={idPronta} role="listbox" aria-label="Respostas prontas">
              {opcoesProntas.map((r, i) => (
                <li key={r.id} id={`${idPronta}-${i}`} role="option" aria-selected={i === pronta}
                  className={i === pronta ? 'marcado' : undefined}
                  onMouseDown={(e) => { e.preventDefault(); setTexto(r.texto); }}>
                  <b>{r.titulo}</b>
                  {r.atalho && <span className="det">/{r.atalho}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
        {anexando && (
          <ZonaDeArquivo rotulo="Anexo" tipos="PDF, imagem, áudio ou documento, até 25 MB"
            desabilitado={subindo !== null} aoEscolher={(fs) => void subir(fs[0])}
            textoVazio={subindo !== null ? `Enviando o anexo… ${Math.round(subindo * 100)} %` : undefined} />
        )}
        {anexo ? (
          <div className="compositor-anexo">
            <span>{anexo.nome} <span className="msg-aux">{tamanho(anexo.tamanho)}</span></span>
            <button type="button" className="botao discreto" onClick={() => setAnexo(null)}>Tirar</button>
          </div>
        ) : !anexando && (
          <div>
            <button type="button" className="botao secundario" onClick={() => setAnexando(true)}>Anexar um arquivo</button>
          </div>
        )}

        {mostraOportunidade && (
          <label className="caixa-marcar nova-conversa-oportunidade">
            <input type="checkbox" checked={abrirOportunidade} onChange={(e) => setAbrirOportunidade(e.target.checked)} />
            <span>
              <strong>Abrir oportunidade</strong>
              <span className="sub">
                {pessoa?.oportunidadeAbertaId && !abrirOportunidade
                  ? 'Ele já tem oportunidade aberta: a conversa liga à mais recente, e o painel da conversa permite trocar.'
                  : 'Em Novo, com você como responsável. Desmarque se não for um pedido de serviço.'}
              </span>
            </span>
          </label>
        )}
        <p className="janela-explica">
          Você fica como responsável pela conversa, e o agente não responde por cima. Se o número não tiver WhatsApp, a
          mensagem aparece na conversa como «não enviada».
        </p>
        <ErroDaApi erro={erro} />
      </div>
      <Rodape acao="Enviar" falta={falta} ocupado={ocupado} aoCancelar={aoFechar} aoAgir={() => void enviar()} />
    </Dialogo>
  );
}
